import React, { useState } from 'react';
import { Building2, RefreshCw, HelpCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { GoogleBusinessSetupModal } from './GoogleBusinessSetupModal';
import { getUserApiCredentials } from '../lib/apiCredentials';

interface GoogleBusinessConnectProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  hasConnectedAccounts?: boolean;
}

export function GoogleBusinessConnect({ onSuccess, onError, hasConnectedAccounts }: GoogleBusinessConnectProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);

  const handleGoogleBusinessLogin = async () => {
    setIsLoading(true);
    try {
      // Get user's custom credentials or use defaults
      const credentials = await getUserApiCredentials();
      const clientId = credentials.google_business_client_id;

      if (!clientId) {
        toast.error('Google Business Client ID not configured. Please add your credentials in API Settings.');
        setIsLoading(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/google-business-oauth`;

      // Google Business Profile API scopes
      const scopes = [
        'https://www.googleapis.com/auth/business.manage',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile',
      ];

      // Store the current URL in localStorage for redirect after auth
      localStorage.setItem('google_business_auth_redirect', window.location.href);

      // Redirect to Google OAuth
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scopes.join(' '));
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      authUrl.searchParams.set('state', Math.random().toString(36).substring(7));

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Google Business login error:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to connect Google Business account');
      toast.error('Failed to connect Google Business account');
      setIsLoading(false);
    }
  };

  return (
    <>
      <GoogleBusinessSetupModal isOpen={showSetupModal} onClose={() => setShowSetupModal(false)} />

      <div className="space-y-2">
        <button
          onClick={handleGoogleBusinessLogin}
          disabled={isLoading}
          className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg
            text-gray-500 hover:text-red-600 hover:border-red-600 w-full justify-center
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Building2 className="h-5 w-5" />
              <span>{hasConnectedAccounts ? 'Connect Another Google Business' : 'Connect Google Business'}</span>
            </>
          )}
        </button>

        <button
          onClick={() => setShowSetupModal(true)}
          className="flex items-center justify-center space-x-2 w-full text-sm text-red-600 hover:text-red-700 py-1"
        >
          <HelpCircle className="w-4 h-4" />
          <span>How to set up Google Business integration?</span>
        </button>
      </div>
    </>
  );
}
